import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { useSnackbar } from 'hooks/use-snackbar';
import { getLocations } from 'api/warehouse/get-locations';
import { GetLocationsVm } from 'api/warehouse/get-locations.vm';
import { LocationVm } from 'api/warehouse/location.vm';
import { createLocation } from 'api/warehouse/create-location';
import { useSearchParams } from 'react-router-dom';

const LocationsPage = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [locations, setLocations] = useState<LocationVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const warehouseId = searchParams.get('warehouseId');

  const fetchLocations = async (offset: string | null = null, reset: boolean = false) => {
    if (!warehouseId) return;

    setLoading(true);
    getLocations(warehouseId, offset || '', {
      success: (response: GetLocationsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setLocations(reset ? response.objects : [...locations, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter localizações:', err);
        snackbar.show('Falha ao carregar localizações', null, 'error');
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
        if (response.objects.length) {
          setSearchParams((prev) => {
            prev.set('warehouseId', response.objects[0].id);
            return prev;
          });
        }
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
        snackbar.show('Falha ao carregar armazéns', null, 'error');
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    if (warehouseId) {
      fetchLocations(null, true);
    } else {
      setLocations([]);
    }
  }, [warehouseId]);

  const handleCreateLocation = async () => {
    createLocation(
      warehouseId!!,
      { id: currentLocation!! },
      {
        success: () => {
          snackbar.show('Localização criada com sucesso', null, 'success');
          fetchLocations(null, true);
          setOpenDialog(false);
        },
        error: () => {
          snackbar.show('Falha ao guardar localização', null, 'error');
        },
      }
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Localizações
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setCurrentLocation(null);
            setOpenDialog(true);
          }}
          disabled={!warehouseId}
        >
          Adicionar Localização
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Selecionar Armazém</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseId || ''}
                label="Selecionar Armazém"
                onChange={(e) => {
                  setSearchParams((prev) => {
                    prev.set('warehouseId', e.target.value);
                    return prev;
                  });
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{warehouse.name}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchParams((prev) => {
                  prev.delete('warehouseId');
                  return prev;
                });
              }}
              fullWidth
              startIcon={<ClearIcon />}
              disabled={!warehouseId}
              sx={{ height: '56px' }}
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((location) => (
              <TableRow key={location.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{location.id}</Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={() => fetchLocations(nextOffset)} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : locations.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais localizações para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhuma localização encontrada
          </Typography>
        )}
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentLocation ? 'Editar Localização' : 'Adicionar Nova Localização'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="ID da Localização"
              fullWidth
              value={currentLocation || ''}
              onChange={(e) => setCurrentLocation(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateLocation} disabled={!currentLocation}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationsPage;
